import {
  apiGroupMachinesDetail,
  getCustomInspectionFormTemplates,
  getInspectionFormTemplates,
  getInspectionFormTemplatesAll,
  getInspectionFormTemplatesDetail,
} from "@app/api";
import {
  Button,
  DataDialog,
  DataTooltip,
  FileUploadButton,
} from "@app/components/atoms";
import { SelectMachineEnum } from "@app/constants";
import { isMobile } from "@app/helpers";
import { useStoreDispatch } from "@app/hooks";
import { setInspectionsTemplate, setMachineId, setShowAlert } from "@app/store";
import {
  CreateInspectionFormProps,
  CustomInspectionFormsType,
  InspectionFormTemplateType,
  TemplateList,
  VehicleNumber,
} from "@app/types";
import {
  Box,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  buildInspectionFormDataFromBulkUploadFile,
  buildInspectionTemplateOptionColumnHeaders,
  INSPECTION_BULK_UPLOAD_ERROR,
  limitInspectionFormItems,
} from "@app/helpers/inspection-bulk-template.helper";

const ALL_MACHINES_VALUE = "ALL_MACHINES";

const attachSourceMachine = (
  templateList: TemplateList,
  machine: VehicleNumber,
): TemplateList => ({
  inspectionFormTemplates: (templateList.inspectionFormTemplates ?? []).map(
    (template) => ({
      ...template,
      sourceMachineId: machine.machineId,
      sourceMachineName: machine.machineName,
    }),
  ),
  customInspectionForms: (templateList.customInspectionForms ?? []).map(
    (template) => ({
      ...template,
      sourceMachineId: machine.machineId,
      sourceMachineName: machine.machineName,
    }),
  ),
});

const normalizeTemplateListSource = (
  templateList: TemplateList,
  fallbackMachine?: VehicleNumber,
): TemplateList => ({
  inspectionFormTemplates: (templateList.inspectionFormTemplates ?? []).map(
    (template) => ({
      ...template,
      sourceMachineId:
        template.sourceMachineId ??
        template.machineId ??
        fallbackMachine?.machineId,
      sourceMachineName:
        template.sourceMachineName ??
        template.machineName ??
        fallbackMachine?.machineName,
    }),
  ),
  customInspectionForms: (templateList.customInspectionForms ?? []).map(
    (template) => ({
      ...template,
      sourceMachineId:
        template.sourceMachineId ??
        template.machineId ??
        fallbackMachine?.machineId,
      sourceMachineName:
        template.sourceMachineName ??
        template.machineName ??
        fallbackMachine?.machineName,
    }),
  ),
});

const findSelectedTemplate = (
  templateList: TemplateList | undefined,
  selectedTemplateId: string,
): InspectionFormTemplateType | CustomInspectionFormsType | undefined => {
  if (!templateList) {
    return undefined;
  }

  return (
    templateList.inspectionFormTemplates.find(
      (template) => template.inspectionFormTemplateId === selectedTemplateId,
    ) ??
    templateList.customInspectionForms.find(
      (template) => template.customInspectionFormId === selectedTemplateId,
    )
  );
};

const isInspectionFormTemplate = (
  template: InspectionFormTemplateType | CustomInspectionFormsType,
): template is InspectionFormTemplateType => {
  return "inspectionFormTemplateId" in template;
};

const getBulkUploadErrorTranslationKey = (error: unknown) => {
  const message = error instanceof Error ? error.message : "";

  switch (message) {
    case INSPECTION_BULK_UPLOAD_ERROR.NO_INFORMATION:
      return "Thefilehasnoinformationsapartfromheaders";
    case INSPECTION_BULK_UPLOAD_ERROR.NO_VALID_DATA:
      return "Thefilecontainsnovaliddata";
    case INSPECTION_BULK_UPLOAD_ERROR.INVALID_TEMPLATE_FORMAT:
      return "Invalid template format";
    default:
      return "Anunexpectederroroccurredwhileprocessingthefile";
  }
};

export function CreateInspectionForm({
  open,
  setOpen,
  clickSubmit,
  mode = "create",
  onTemplateDownload,
  isoLocaleCode = "en",
}: CreateInspectionFormProps) {
  const dispatch = useStoreDispatch();
  const { t } = useTranslation();
  const [dbMachineList, setDbMachineList] = useState<VehicleNumber[]>();
  const [dbTemplateList, setDbTemplateList] = useState<TemplateList>();
  const [selectedExcelFile, setSelectedExcelFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { id } = useParams();
  const isDownloadMode = mode === "download";
  const isUploadMode = mode === "upload";
  const {
    register,
    control,
    setValue,
    reset,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      vehicleNumber: {
        machineId: "",
        machineName: "",
      },
      selectedTemplateId: "",
    },
  });
  const vehicleNumber = watch("vehicleNumber");
  const selectedTemplateId = watch("selectedTemplateId");
  const selectedMachineId = vehicleNumber?.machineId ?? "";
  const selectedMachineName = vehicleNumber?.machineName ?? "";

  const resetDb = () => {
    setDbMachineList(undefined);
    setDbTemplateList(undefined);
    setSelectedExcelFile(null);
    setIsSubmitting(false);
    reset();
  };

  const handleChangeSelectedTemplateId = (event: SelectChangeEvent) => {
    setValue("selectedTemplateId", event.target.value);
  };

  const handleChangeExcelFile = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const validExtensions = [".xlsx", ".xls"];
    const fileExtension = file.name
      .slice(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      dispatch(
        setShowAlert(t("InvalidfiletypePleaseuploadanExcelfilexlsxorxls")),
      );
      event.target.value = "";
      setSelectedExcelFile(null);
      return;
    }

    setSelectedExcelFile(file);
  };

  const onSubmit = async (data: any) => {
    if (isUploadMode) {
      if (!data?.vehicleNumber?.machineId || !selectedExcelFile) {
        return;
      }

      try {
        setIsSubmitting(true);
        const db = await buildInspectionFormDataFromBulkUploadFile(
          selectedExcelFile,
          {
            headers: {
              itemName: t("itemName"),
              description: t("explanation"),
              answerOption: t("AnswerOptions"),
              predefinedOptionColumns:
                buildInspectionTemplateOptionColumnHeaders(t),
            },
            answerOptions: {
              okOrAnomary: t("ANOMARY"),
              okAnomaryNotApplicable: t("ANOMARYNOTAPP"),
              commentFile: t("CommentFile"),
              predefinedComment: t("predefinedCommentSelection"),
            },
            meterItems: {
              serviceMeter: t("serviceMeterOrSMR"),
              odometer: t("odometer"),
            },
          },
        );

        handleAfterSubmit(data, db);
      } catch (error) {
        dispatch(setShowAlert(t(getBulkUploadErrorTranslationKey(error))));
      } finally {
        setIsSubmitting(false);
      }

      return;
    }

    const selectedTemplate = findSelectedTemplate(
      dbTemplateList,
      selectedTemplateId,
    );
    const sourceMachineId =
      data?.vehicleNumber?.machineId || selectedTemplate?.sourceMachineId;

    if (!id || !selectedTemplate || !sourceMachineId) {
      return;
    }

    try {
      setIsSubmitting(true);

      const db = isInspectionFormTemplate(selectedTemplate)
        ? await getInspectionFormTemplatesDetail(
            id,
            sourceMachineId,
            selectedTemplate.inspectionFormTemplateId,
          )
        : await getCustomInspectionFormTemplates(
            id,
            sourceMachineId,
            selectedTemplate.customInspectionFormId,
          );

      if (isDownloadMode) {
        await onTemplateDownload?.({
          machineId: sourceMachineId,
          machineName:
            data?.vehicleNumber?.machineName ||
            selectedTemplate.sourceMachineName,
          selectedTemplate,
          templateData: db,
        });
        resetDb();
        setOpen(false);
        return;
      }

      handleAfterSubmit(data, db);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAfterSubmit = (data: any, db: any) => {
    clickSubmit?.();
    dispatch(
      setInspectionsTemplate({
        ...db,
        inspectionItems: limitInspectionFormItems(db?.inspectionItems ?? []),
      }),
    );
    dispatch(setMachineId(data?.vehicleNumber?.machineId));
    resetDb();
    setOpen(false);
  };

  useEffect(() => {
    if (!!id && !!open) {
      apiGroupMachinesDetail(id, {
        select: [SelectMachineEnum.MACHINE_NAME],
      })
        .then((db) => {
          setDbMachineList(db?.data?.data as VehicleNumber[]);
        })
        .catch(() => setOpen(false));
    }
  }, [id, open, setOpen]);

  useEffect(() => {
    if (!open || !id || isUploadMode) {
      return;
    }

    const loadTemplateList = async () => {
      setValue("selectedTemplateId", "");
      setDbTemplateList(undefined);

      try {
        if (selectedMachineId) {
          const selectedMachine = {
            machineId: selectedMachineId,
            machineName: selectedMachineName,
          };
          const db = await getInspectionFormTemplates(id, selectedMachineId);

          setDbTemplateList(attachSourceMachine(db, selectedMachine));
          return;
        }

        if (isDownloadMode) {
          const fallbackMachine = dbMachineList?.[0];

          if (!fallbackMachine?.machineId) {
            return;
          }

          const db = await getInspectionFormTemplatesAll(
            id,
            fallbackMachine.machineId,
          );

          setDbTemplateList(normalizeTemplateListSource(db, fallbackMachine));
        }
      } catch {
        setDbTemplateList(undefined);
      }
    };

    loadTemplateList();
  }, [
    id,
    isDownloadMode,
    isUploadMode,
    open,
    setValue,
    dbMachineList,
    selectedMachineId,
    selectedMachineName,
  ]);

  const machineLabel = isDownloadMode
    ? t("groupDetailTab.machineTab")
    : t("customerVehicleNumber");
  const machineLabelWithRequired = `${machineLabel}${
    isDownloadMode ? "" : "*"
  }`;
  const templateLabelWithRequired = `${t("template")}*`;
  const submitText = isDownloadMode
    ? t("formAction.downloadexcel")
    : t("formAction.create");
  const dialogTitle = isDownloadMode
    ? t("formAction.downloadexcel")
    : isUploadMode
      ? t("formAction.uploadexcel")
      : t("addInspection");
  const selectedVehicleValue = selectedMachineId;
  const hasTemplateOptions =
    !!dbTemplateList?.inspectionFormTemplates.length ||
    !!dbTemplateList?.customInspectionForms.length;
  const isSubmitDisabled = isUploadMode
    ? !selectedMachineId || !selectedExcelFile || isSubmitting
    : !selectedTemplateId || isSubmitting || !hasTemplateOptions;

  useEffect(() => {
    if (!open) {
      resetDb();
    }
  }, [open]);

  return (
    <>
      <DataDialog
        title={dialogTitle}
        open={open}
        setOpen={setOpen}
        disableBackdropClick={true}
      >
        <Box
          component="form"
          autoComplete="off"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormControl
            fullWidth
            className="mt-10 select-field"
            error={!!errors.vehicleNumber?.type}
          >
            <InputLabel
              id={`${
                !!errors.vehicleNumber?.type
                  ? "demo-simple-select-error-label"
                  : "demo-simple-select-label"
              }`}
              htmlFor="grouped-select"
            >
              {machineLabelWithRequired}
            </InputLabel>
            <Controller
              name="vehicleNumber"
              control={control}
              rules={{
                validate: {
                  required: (value) => isDownloadMode || !!value?.machineId,
                },
              }}
              render={({ field }) => (
                <Select
                  labelId={`${
                    !!errors.vehicleNumber?.type
                      ? "demo-simple-select-error-label"
                      : "demo-simple-select-label"
                  }`}
                  id={`${
                    !!errors.vehicleNumber?.type
                      ? "demo-simple-select-error"
                      : "demo-simple-select"
                  }`}
                  value={
                    isDownloadMode && !field.value?.machineId
                      ? ALL_MACHINES_VALUE
                      : (field.value?.machineId ?? selectedVehicleValue)
                  }
                  label={machineLabelWithRequired}
                  onChange={(event: SelectChangeEvent<string>) => {
                    const machineId = event.target.value;
                    const selectedMachine =
                      machineId === ALL_MACHINES_VALUE
                        ? {
                            machineId: "",
                            machineName: "",
                          }
                        : (dbMachineList?.find(
                            (machine) => machine.machineId === machineId,
                          ) ?? {
                            machineId: "",
                            machineName: "",
                          });

                    field.onChange(selectedMachine);
                  }}
                  onBlur={field.onBlur}
                  error={!!errors.vehicleNumber?.type}
                  onClose={() => trigger("vehicleNumber")}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 216,
                        width: isMobile() ? window.innerWidth - 80 : 512,
                      },
                    },
                  }}
                >
                  {isDownloadMode && (
                    <MenuItem
                      value={ALL_MACHINES_VALUE}
                      sx={{ color: "#1b232a", padding: "8px 12px" }}
                    >
                      {t("all")}
                    </MenuItem>
                  )}
                  {!!dbMachineList &&
                    dbMachineList?.map((option: any) => (
                      <MenuItem
                        key={option.machineId}
                        value={option.machineId}
                        sx={{ color: "#1b232a", padding: "8px 12px" }}
                      >
                        <DataTooltip isTextPre width="100%">
                          {option.machineName}
                        </DataTooltip>
                      </MenuItem>
                    ))}
                </Select>
              )}
            />
            {errors?.vehicleNumber?.type ? (
              <span className="txt-red fs-12 mt-5 ml-16">
                {errors.vehicleNumber?.type === "required" &&
                  t("messages.dropdownRequire")}
              </span>
            ) : !isDownloadMode ? (
              <span className="fs-12 mt-5 ml-16 txt-gray-1">
                &#42;{t("required")}
              </span>
            ) : null}
          </FormControl>
          {!isUploadMode && (
            <FormControl
              fullWidth
              className="mt-24 select-field"
              error={!!errors.selectedTemplateId?.type}
            >
              <InputLabel
                id={`${
                  !!errors.selectedTemplateId?.type
                    ? "demo-simple-select-error-label"
                    : "demo-simple-select-label"
                }`}
                htmlFor="grouped-select"
              >
                {templateLabelWithRequired}
              </InputLabel>
              <Select
                {...register("selectedTemplateId", {
                  validate: {
                    required: (value) => !!value,
                  },
                })}
                onChange={handleChangeSelectedTemplateId}
                value={selectedTemplateId}
                defaultValue=""
                id={`${
                  !!errors.selectedTemplateId?.type
                    ? "demo-simple-select-error"
                    : "grouped-select"
                }`}
                label={templateLabelWithRequired}
                disabled={!isDownloadMode && !vehicleNumber.machineId}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 216,
                      width: isMobile() ? window.innerWidth - 80 : 512,
                    },
                  },
                }}
                error={!!errors.selectedTemplateId?.type}
                onClose={() => trigger("selectedTemplateId")}
              >
                {!!dbTemplateList?.inspectionFormTemplates.length && (
                  <ListSubheader>{t("inspectionFormTemplates")}</ListSubheader>
                )}
                {!!dbTemplateList &&
                  dbTemplateList?.inspectionFormTemplates?.map(
                    (option: any) => (
                      <MenuItem
                        key={option?.inspectionFormTemplateId}
                        value={option?.inspectionFormTemplateId}
                        sx={{ color: "#1b232a", padding: "8px 12px" }}
                      >
                        <DataTooltip isTextPre width="100%">
                          {option.inspectionFormTemplateName}
                        </DataTooltip>
                      </MenuItem>
                    ),
                  )}

                {!!dbTemplateList?.customInspectionForms.length && (
                  <ListSubheader>{t("customInspectionForms")}</ListSubheader>
                )}
                {!!dbTemplateList &&
                  dbTemplateList?.customInspectionForms?.map((option: any) => (
                    <MenuItem
                      key={option?.customInspectionFormId}
                      value={option?.customInspectionFormId}
                    >
                      <DataTooltip isTextPre width="100%">
                        {option.name}
                      </DataTooltip>
                    </MenuItem>
                  ))}
              </Select>
              {errors?.selectedTemplateId?.type ? (
                <span className="txt-red fs-12 mt-5 ml-16">
                  {errors.selectedTemplateId?.type === "required" &&
                    t("messages.dropdownRequire")}
                </span>
              ) : (
                <span className="fs-12 ml-16 mt-5 txt-gray-1">
                  &#42;{t("required")}
                </span>
              )}
            </FormControl>
          )}

          {isUploadMode && (
            <div className="mt-24">
              <FileUploadButton
                htmlFor="inspection-form-bulk-upload-input"
                className="btn-no-border-dark-blue"
              >
                {t("chooseFile")}
              </FileUploadButton>
              <input
                id="inspection-form-bulk-upload-input"
                type="file"
                accept=".xlsx, .xls"
                style={{ display: "none" }}
                onChange={(event) => {
                  handleChangeExcelFile(event);
                  event.target.value = "";
                }}
              />
              {selectedExcelFile?.name && (
                <span className="fs-12 ml-16 txt-gray-1">
                  {selectedExcelFile.name}
                </span>
              )}
              <div className="fs-12 ml-16 mt-5 txt-gray-1">
                &#42;{t("required")}
              </div>
            </div>
          )}
          <div className="flex align-items-center justify-content-end mt-24">
            <Button
              className="btn-no-border"
              onClick={() => {
                resetDb();
                setOpen(false);
              }}
            >
              {t("dialogReport.cancel")}
            </Button>
            <Button
              className="btn-primary ml-16"
              disabled={isSubmitDisabled}
              type="submit"
            >
              {submitText}
            </Button>
          </div>
        </Box>
      </DataDialog>
    </>
  );
}
